import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { ArrowDown, ArrowUp, Eye, Link2, Pencil, Plus, Printer, Save, Trash2, Unlink, X } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import {
    Badge, Button, Checkbox, DataTable, FormActions, FormDrawer, FormSection,
    IconButton, InfoBar, Input, Modal, PageHeader, Select, Spin, Switch,
    TableStatusBar, Textarea, useAlert
} from '../ui';
import { DataTableColumn } from '../ui/DataTable';

type TypeChamp = 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'CHECKBOX' | 'TEXTAREA'

interface ChampForm {
    CodeChamp: string
    Libelle: string
    TypeChamp: TypeChamp
    Obligatoire: boolean
    Placeholder: string
    RegexValidation: string
    OptionsJson: string
    ValeurDefaut: string
}

const TYPE_CHAMPS: { value: TypeChamp; label: string }[] = [
    { value: 'TEXT', label: 'Texte' },
    { value: 'NUMBER', label: 'Nombre' },
    { value: 'DATE', label: 'Date' },
    { value: 'SELECT', label: 'Liste déroulante' },
    { value: 'CHECKBOX', label: 'Case à cocher' },
    { value: 'TEXTAREA', label: 'Texte multiligne' },
]

const TYPES_CONNUS: TypeChamp[] = ['TEXT', 'NUMBER', 'DATE', 'SELECT', 'CHECKBOX', 'TEXTAREA']

/** Formate une date/heure en fr-FR, tolérant les valeurs invalides. */
function formatDate(v: any): string {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return String(v)
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Extrait la liste des champs depuis un Result GET :
 * {Formulaire, Champs} (doc), tableau direct de champs,
 * tableau contenant un objet { Champs }, ou objet { Champs }.
 */
function extractChampsList(result: any): any[] {
    if (!result) return []
    if (Array.isArray(result)) {
        if (result.some((x: any) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined))) {
            return result.filter((x: any) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined))
        }
        const sub = result.find((x: any) => Array.isArray(x?.Champs))
        return sub?.Champs || []
    }
    if (typeof result === 'object') {
        if (Array.isArray(result.Champs)) return result.Champs
    }
    return []
}

/** Extrait l'en-tête (CodeForm, Libelle, Description) depuis un Result GET ({Formulaire, Champs} ou formes tolérées). */
function extractHeader(result: any, fallback: any): any {
    if (result && !Array.isArray(result) && typeof result === 'object') {
        const f = result.Formulaire && typeof result.Formulaire === 'object' ? result.Formulaire : result
        return {
            CodeForm: f.CodeForm ?? fallback?.CodeForm,
            Libelle: f.Libelle ?? fallback?.Libelle,
            Description: f.Description ?? fallback?.Description,
        }
    }
    if (Array.isArray(result)) {
        const head = result.find((x: any) => x && x.CodeForm !== undefined && x.CodeChamp === undefined)
        if (head) {
            return {
                CodeForm: head.CodeForm ?? fallback?.CodeForm,
                Libelle: head.Libelle ?? fallback?.Libelle,
                Description: head.Description ?? fallback?.Description,
            }
        }
    }
    return fallback
}

/** Normalise la liste des champs d'un formulaire (triée par Ordre). */
function normalizeChamps(result: any): ChampForm[] {
    return extractChampsList(result)
        .map((c: any) => {
            const t = String(c?.TypeChamp ?? 'TEXT').toUpperCase()
            return {
                Ordre: c?.Ordre ?? 0,
                CodeChamp: c?.CodeChamp ?? '',
                Libelle: c?.Libelle ?? '',
                TypeChamp: (TYPES_CONNUS.includes(t as TypeChamp) ? t : 'TEXT') as TypeChamp,
                Obligatoire: !!c?.Obligatoire,
                Placeholder: c?.Placeholder ?? '',
                RegexValidation: c?.RegexValidation ?? '',
                OptionsJson: c?.OptionsJson ?? '',
                ValeurDefaut: c?.ValeurDefaut ?? '',
            }
        })
        .sort((a: any, b: any) => (a.Ordre || 0) - (b.Ordre || 0))
        .map(({ Ordre, ...rest }: any) => rest as ChampForm)
}

/** Parse ReponseJson (chaîne JSON) pour affichage. */
function parseReponseJson(raw: any): any {
    if (raw == null) return null
    if (typeof raw === 'object') return raw
    try { return JSON.parse(raw) } catch { return raw }
}

/** Rendu lisible des paires clé/valeur d'une réponse. */
function ReponseCell({ raw }: { raw: any }) {
    const parsed = parseReponseJson(raw)
    if (parsed == null) return <span className="text-xs text-[var(--eb-text-muted)]">—</span>
    if (typeof parsed !== 'object') return <span className="text-xs">{String(parsed)}</span>
    const entries = Object.entries(parsed)
    if (entries.length === 0) return <span className="text-xs text-[var(--eb-text-muted)]">—</span>
    return (
        <div className="flex flex-col gap-0.5 py-0.5">
            {entries.map(([k, v]) => (
                <span key={k} className="text-xs leading-snug">
                    <span className="font-semibold text-[var(--eb-text-muted)]">{k} : </span>
                    <span className="text-[var(--eb-text)]">{v == null ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v))}</span>
                </span>
            ))}
        </div>
    )
}

/* ------------------------------------------------------------------ */
/*  Options SELECT — panier de puces (sérialisé en OptionsJson)        */
/* ------------------------------------------------------------------ */

/** Parse OptionsJson (chaîne JSON) → tableau de chaînes. */
function parseOptions(json: any): string[] {
    if (Array.isArray(json)) return json.map(String).filter(Boolean)
    if (!json) return []
    try {
        const v = JSON.parse(json)
        return Array.isArray(v) ? v.map(String).filter(Boolean) : []
    } catch {
        return []
    }
}

/**
 * Éditeur d'options en mode « panier » : saisie + Entrée ou bouton Ajouter,
 * puces supprimables. Sérialisé en OptionsJson (tableau JSON de chaînes).
 */
function OptionsPanier(props: { options: string[]; onChange: (opts: string[]) => void }) {
    const [draft, setDraft] = useState('')

    const add = () => {
        const v = draft.trim()
        if (!v || props.options.includes(v)) return
        props.onChange([...props.options, v])
        setDraft('')
    }

    return (
        <div>
            <label className="block text-xs font-semibold text-[var(--eb-text-muted)] mb-1.5">
                Options de la liste *
            </label>
            <div className="flex gap-2">
                <div className="flex-1">
                    <Input
                        type='text'
                        placeholder="Tapez une option puis Entrée (ex : CNI)"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') { e.preventDefault(); add(); }
                            e.stopPropagation();
                        }}
                    />
                </div>
                <Button type='button' variant='outline' size='sm' onClick={add} disabled={!draft.trim()}>
                    Ajouter
                </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2 min-h-[26px]">
                {props.options.length === 0 && (
                    <span className="text-[11px] text-[var(--eb-text-muted)] italic">Aucune option — ajoutez-en au moins une</span>
                )}
                {props.options.map((opt, i) => (
                    <span key={opt + i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--eb-primary-soft)] text-[var(--eb-primary)]">
                        {opt}
                        <button
                            type='button'
                            className="hover:opacity-70"
                            onClick={() => props.onChange(props.options.filter((_, j) => j !== i))}
                            title={`Retirer ${opt}`}
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    )
}

/* ------------------------------------------------------------------ */
/*  Validation — presets lisibles reformattés en regex                 */
/* ------------------------------------------------------------------ */

const REGEX_CUSTOM = '__CUSTOM__';

const REGEX_PRESETS: { value: string; label: string; exemple: string }[] = [
    { value: '', label: 'Aucune validation', exemple: '' },
    { value: '^\\S+@\\S+\\.\\S+$', label: 'Adresse email', exemple: 'nom@domaine.com' },
    { value: '^\\+?[0-9]{8,15}$', label: 'Téléphone international (8–15 chiffres)', exemple: '+22507070707' },
    { value: '^[0-9]{8}$', label: 'Numéro à 8 chiffres', exemple: '07070707' },
    { value: '^[0-9]{10}$', label: 'Numéro à 10 chiffres', exemple: '0707070707' },
    { value: '^[0-9]+(\\.[0-9]+)?$', label: 'Nombre (décimal autorisé)', exemple: '1500.5' },
    { value: "^[A-Za-zÀ-ÿ' -]+$", label: 'Lettres uniquement (nom, prénom…)', exemple: 'KOUASSI' },
    { value: '^[A-Za-z0-9]+$', label: 'Alphanumérique sans espaces', exemple: 'ABC123' },
    { value: REGEX_CUSTOM, label: 'Personnalisé (regex avancée)…', exemple: '' },
];

/** Retrouve le preset correspondant à la regex courante ('' = aucune, '__CUSTOM__' = manuelle). */
function regexPresetOf(rx: string): string {
    if (!rx) return ''
    return REGEX_PRESETS.some(p => p.value === rx) ? rx : REGEX_CUSTOM
}

/**
 * Sélecteur de validation compréhensible : preset métier → regex générée,
 * ou regex personnalisée (la regex courante — preset inclus — sert de brouillon
 * éditable). Testeur en direct + regex résultante affichée.
 */
function RegexField(props: { value: string; onChange: (v: string) => void }) {
    // Mode « personnalisé » conservé en état local — sinon une valeur vide
    // recalculerait le preset « Aucune validation » et masquerait le champ.
    const [customMode, setCustomMode] = useState(() => regexPresetOf(props.value) === REGEX_CUSTOM)
    const [test, setTest] = useState('')

    const preset = customMode ? REGEX_CUSTOM : regexPresetOf(props.value)

    let testState: 'none' | 'ok' | 'ko' = 'none'
    if (props.value && test) {
        try { testState = new RegExp(props.value).test(test) ? 'ok' : 'ko' }
        catch { testState = 'ko' }
    }

    const exemple = REGEX_PRESETS.find(p => p.value === preset)?.exemple

    return (
        <div className="flex flex-col gap-1.5">
            <Select
                label="Validation de la saisie"
                value={preset}
                onChange={(v: string) => {
                    if (v === REGEX_CUSTOM) {
                        // Garde la regex courante (preset ou non) comme brouillon éditable
                        setCustomMode(true)
                    } else {
                        setCustomMode(false)
                        props.onChange(v)
                    }
                }}
                options={REGEX_PRESETS}
            />
            {preset === REGEX_CUSTOM && (
                <Input
                    type='text'
                    placeholder="Ex : ^[0-9]{8}$"
                    value={props.value}
                    helperText="Expression régulière complète (ex : ^[0-9]{8}$ pour 8 chiffres)"
                    onChange={(e: any) => props.onChange(e.target.value)}
                />
            )}
            {props.value && (
                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <Input
                            type='text'
                            placeholder={exemple ? `Tester… ex : ${exemple}` : 'Tester une valeur…'}
                            value={test}
                            onChange={(e: any) => setTest(e.target.value)}
                        />
                    </div>
                    {testState === 'ok' && <Badge color='green'>Valide</Badge>}
                    {testState === 'ko' && <Badge color='red'>Invalide</Badge>}
                </div>
            )}
            {props.value && preset !== REGEX_CUSTOM && (
                <code className="text-[10px] text-[var(--eb-text-muted)] break-all">Regex générée : {props.value}</code>
            )}
        </div>
    )
}

/* ------------------------------------------------------------------ */
/*  Aperçu global du formulaire (vue client)                           */
/* ------------------------------------------------------------------ */

/**
 * Rend le formulaire complet comme côté client : libellé, description,
 * champs interactifs dans l'ordre, bouton de validation simulé.
 */
function FormPreviewModal(props: { open: boolean; onClose: () => void; libelle: string; description: string; champs: ChampForm[] }) {
    const [values, setValues] = useState<Record<string, any>>({})
    const set = (k: string, v: any) => setValues(prev => ({ ...prev, [k]: v }))

    // Réinitialise les valeurs à chaque ouverture
    useEffect(() => { if (props.open) setValues({}) }, [props.open])

    return (
        <Modal open={props.open} onClose={props.onClose} title="Aperçu du formulaire" width={560}>
            {/* Cadre « écran client » */}
            <div className="rounded-xl border border-[var(--eb-primary)]/25 bg-[var(--eb-primary-soft)]/30 p-4">
                <div className="rounded-lg bg-[var(--eb-surface)] border border-[var(--eb-border)] p-5 shadow-sm">
                    <div className="mb-4 pb-3 border-b border-[var(--eb-border)]">
                        <h3 className="text-base font-bold text-[var(--eb-text)]">
                            {props.libelle || 'Titre du formulaire'}
                        </h3>
                        {props.description && (
                            <p className="text-xs text-[var(--eb-text-muted)] mt-1 leading-relaxed">{props.description}</p>
                        )}
                    </div>

                    {props.champs.length === 0 && (
                        <p className="text-xs text-[var(--eb-text-muted)] italic">Aucun champ défini pour l'instant.</p>
                    )}

                    <div className="space-y-3.5">
                        {props.champs.map((c, i) => {
                            const key = c.CodeChamp || `champ_${i}`
                            const label = (
                                <>
                                    {c.Libelle || `Champ ${i + 1}`}
                                    {c.Obligatoire && <span className="text-red-500"> *</span>}
                                </>
                            )
                            if (c.TypeChamp === 'SELECT') {
                                return (
                                    <Select
                                        key={key}
                                        label={label}
                                        value={values[key] ?? ''}
                                        onChange={(v: string) => set(key, v)}
                                        placeholder={c.Placeholder || 'Sélectionner…'}
                                        options={parseOptions(c.OptionsJson).map(o => ({ value: o, label: o }))}
                                    />
                                )
                            }
                            if (c.TypeChamp === 'CHECKBOX') {
                                return (
                                    <Checkbox
                                        key={key}
                                        checked={!!values[key]}
                                        onChange={(v: boolean) => set(key, v)}
                                        label={label}
                                    />
                                )
                            }
                            if (c.TypeChamp === 'TEXTAREA') {
                                return (
                                    <Textarea
                                        key={key}
                                        label={label}
                                        placeholder={c.Placeholder || ''}
                                        value={values[key] ?? c.ValeurDefaut ?? ''}
                                        onChange={(e: any) => set(key, e.target.value)}
                                        rows={3}
                                    />
                                )
                            }
                            return (
                                <Input
                                    key={key}
                                    type={c.TypeChamp === 'NUMBER' ? 'number' : c.TypeChamp === 'DATE' ? 'date' : 'text'}
                                    label={label}
                                    placeholder={c.Placeholder || c.ValeurDefaut || ''}
                                    value={values[key] ?? ''}
                                    onChange={(e: any) => set(key, e.target.value)}
                                />
                            )
                        })}
                    </div>

                    {props.champs.length > 0 && (
                        <div className="mt-5 pt-3 border-t border-[var(--eb-border)] flex justify-end">
                            <Button variant='primary' size='sm' disabled title="Aperçu — la soumission est désactivée">
                                Valider
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <p className="text-[11px] text-[var(--eb-text-muted)] mt-2 text-center">
                Aperçu interactif — les saisies ne sont pas envoyées au serveur.
            </p>
        </Modal>
    )
}

/* ------------------------------------------------------------------ */
/*  Aperçu en direct du champ                                          */
/* ------------------------------------------------------------------ */

function ChampPreview({ champ }: { champ: ChampForm }) {
    const opts = parseOptions(champ.OptionsJson)
    const [previewVal, setPreviewVal] = useState('')
    const [previewCheck, setPreviewCheck] = useState(false)
    return (
        <div className="rounded-xl border border-[var(--eb-primary)]/25 bg-[var(--eb-primary-soft)]/40 p-3">
            <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--eb-primary)]">
                <Eye size={12} />
                Aperçu — tel que le client le verra
            </div>
            <div className="rounded-lg bg-[var(--eb-surface)] border border-[var(--eb-border)] p-3">
                <label className="block text-xs font-semibold text-[var(--eb-text-muted)] mb-1">
                    {champ.Libelle || 'Libellé du champ'}
                    {champ.Obligatoire && <span className="text-red-500"> *</span>}
                </label>
                {champ.TypeChamp === 'SELECT' ? (
                    <Select
                        value={previewVal}
                        onChange={setPreviewVal}
                        placeholder={champ.Placeholder || 'Sélectionner…'}
                        options={opts.map(o => ({ value: o, label: o }))}
                    />
                ) : champ.TypeChamp === 'CHECKBOX' ? (
                    <Checkbox checked={previewCheck} onChange={setPreviewCheck} label={champ.Placeholder || 'Case à cocher'} />
                ) : champ.TypeChamp === 'TEXTAREA' ? (
                    <Textarea placeholder={champ.Placeholder || ''} value={previewVal} onChange={(e: any) => setPreviewVal(e.target.value)} rows={2} />
                ) : (
                    <Input
                        type={champ.TypeChamp === 'NUMBER' ? 'number' : champ.TypeChamp === 'DATE' ? 'date' : 'text'}
                        placeholder={champ.Placeholder || champ.ValeurDefaut || ''}
                        value={previewVal}
                        onChange={(e: any) => setPreviewVal(e.target.value)}
                    />
                )}
            </div>
        </div>
    )
}

export default function GestionFormulaires() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [dataTri, setDataTri] = useState<any>([])

    const [formSelected, setFormSelected] = useState<any>()

    // Drawer formulaire (création / édition)
    const [open, setOpen] = useState(false)
    const [formulaire, setFormulaire] = useState<any>(null)
    const [codeForm, setCodeForm] = useState('')
    const [libelle, setLibelle] = useState('')
    const [description, setDescription] = useState('')
    const [champs, setChamps] = useState<ChampForm[]>([])
    const [saving, setSaving] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)

    // Référentiels pour les affectations
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [operations, setOperations] = useState<any[]>([])

    // Modal affectations
    const [openAffect, setOpenAffect] = useState(false)
    const [formAffect, setFormAffect] = useState<any>(null)
    const [affectations, setAffectations] = useState<any[]>([])
    const [loadingAffect, setLoadingAffect] = useState(false)
    const [savingAffect, setSavingAffect] = useState(false)
    const [affFiliale, setAffFiliale] = useState('')
    const [affService, setAffService] = useState('')
    const [affOperation, setAffOperation] = useState('')
    const [affObligatoire, setAffObligatoire] = useState(false)
    const [affOrdre, setAffOrdre] = useState<number>(1)

    // Modal suppression
    const [openDelete, setOpenDelete] = useState(false)
    const [toDelete, setToDelete] = useState<any>(null)

    // Modal réponses
    const [openReponses, setOpenReponses] = useState(false)
    const [reponses, setReponses] = useState<any[]>([])
    const [loadingReponses, setLoadingReponses] = useState(false)
    const [formulaireReponses, setFormulaireReponses] = useState<any>(null)

    useEffect(() => {
        LoadData()
        LoadFiliales()
        LoadServices()
        LoadOperations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Appel POST générique : corps chiffré, réponse déchiffrée. */
    function postApi(url: string, payload: any) {
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + url,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };
        return axios.request(config).then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value)
    }

    function LoadData() {
        setLoading(true)
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'LIST',
            CodeAbonne: user.CodeAbonne
        })
            .then((res: any) => {
                setLoading(false)
                if (res.StatutCode == 200) {
                    const list = Array.isArray(res.Result) ? res.Result : []
                    const forms = list.map((g: any) => ({
                        id: g.CodeForm,
                        CodeForm: g.CodeForm,
                        Libelle: g.Libelle,
                        Description: g.Description,
                        DateCreation: g.DateCreation,
                    }))
                    setDataTri(forms)
                }
                else {
                    setDataTri([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error?.message || error)
                setLoading(false)
                console.log(error);
            });
    }

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(res.Result || [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // Result est un tableau PLAT [{CodeS, Libelle, CodeFiliale, ...}]
                    // — tolérer aussi la forme [filiales, services]
                    const svc = Array.isArray(res.Result?.[1]) ? res.Result[1] : (res.Result || [])
                    setServices(svc)
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadOperations() {
        // GET ManageCatalogue — lecture seule : LIST des opérations de l'abonné
        const payload = { Entite: 'OPERATION', Option: 'LIST', CodeAbonne: user.CodeAbonne }
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Operations.ManageCatalogueGet + Crypter.encrypt(JSON.stringify(payload)),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200 || res.StatutCode === undefined) {
                    // Tolérer tableau direct ou enveloppes {Table}/{Table1}
                    const r = res.Result ?? res
                    const list = Array.isArray(r) ? r
                        : Array.isArray(r?.Table) ? r.Table
                        : Array.isArray(r?.Table1) ? r.Table1
                        : []
                    setOperations(list)
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    // ---------- Drawer création / édition ----------
    function openDrawer(f?: any) {
        if (!f) {
            setFormulaire(null)
            setCodeForm('')
            setLibelle('')
            setDescription('')
            setChamps([])
            setOpen(true)
            return
        }
        // Édition : charger le détail (en-tête + champs) via Option GET → {Formulaire, Champs}
        setFormulaire(f)
        setCodeForm(f.CodeForm || '')
        setLibelle(f.Libelle || '')
        setDescription(f.Description || '')
        setChamps([])
        setOpen(true)
        setDetailLoading(true)
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'GET',
            CodeForm: f.CodeForm,
            CodeAbonne: user.CodeAbonne
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    const head = extractHeader(res.Result, f)
                    setFormulaire(head)
                    setCodeForm(head?.CodeForm || f.CodeForm || '')
                    setLibelle(head?.Libelle || '')
                    setDescription(head?.Description || '')
                    setChamps(normalizeChamps(res.Result))
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error?.message || error)
                console.log(error);
            })
            .finally(() => setDetailLoading(false))
    }

    const handleClose = () => {
        setOpen(false);
    };

    // ---------- Éditeur de champs ----------
    const addChamp = () => {
        setChamps(prev => [...prev, {
            CodeChamp: '', Libelle: '', TypeChamp: 'TEXT', Obligatoire: false,
            Placeholder: '', RegexValidation: '', OptionsJson: '', ValeurDefaut: ''
        }])
    }

    const updateChamp = (index: number, patch: Partial<ChampForm>) => {
        setChamps(prev => prev.map((c, i) => i === index ? { ...c, ...patch } : c))
    }

    const removeChamp = (index: number) => {
        setChamps(prev => prev.filter((_, i) => i !== index))
    }

    const moveChamp = (index: number, dir: -1 | 1) => {
        setChamps(prev => {
            const j = index + dir
            if (j < 0 || j >= prev.length) return prev
            const next = [...prev]
            const [item] = next.splice(index, 1)
            next.splice(j, 0, item)
            return next
        })
    }

    // ---------- Sauvegarde ----------
    const onSubmit = (e?: any) => {
        if (e?.preventDefault) e.preventDefault()
        if (!codeForm.trim()) {
            alert.warning('Le code du formulaire est obligatoire')
            return
        }
        if (!libelle.trim()) {
            alert.warning('Le libellé du formulaire est obligatoire')
            return
        }
        setSaving(true)
        const payload = {
            Option: formulaire ? 'UPDATE' : 'CREATE',
            CodeForm: codeForm.trim(),
            CodeAbonne: user.CodeAbonne,
            Libelle: libelle,
            Description: description || '',
            ChampsJson: JSON.stringify(champs.map((c, i) => ({
                Ordre: i + 1,
                CodeChamp: c.CodeChamp,
                Libelle: c.Libelle,
                TypeChamp: c.TypeChamp,
                Obligatoire: !!c.Obligatoire,
                Placeholder: c.Placeholder || '',
                RegexValidation: c.RegexValidation || '',
                OptionsJson: c.OptionsJson || '',
                ValeurDefaut: c.ValeurDefaut || '',
            }))),
        }

        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, payload)
            .then((res: any) => {
                setSaving(false)
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setOpen(false)
                    LoadData()
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setSaving(false)
                alert.error('Error', error?.message || error)
            })
    }

    // ---------- Suppression ----------
    function confirmDelete() {
        if (!toDelete) return
        setLoading(true)
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'DELETE',
            CodeForm: toDelete.CodeForm,
            CodeAbonne: user.CodeAbonne,
        })
            .then((res: any) => {
                setLoading(false)
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setOpenDelete(false)
                    setToDelete(null)
                    LoadData()
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error?.message || error)
            })
    }

    // ---------- Affectations ----------
    function openAffectations(f: any) {
        setFormAffect(f)
        setOpenAffect(true)
        resetAffectForm()
        loadAffectations(f.CodeForm)
    }

    function resetAffectForm() {
        setAffFiliale('')
        setAffService('')
        setAffOperation('')
        setAffObligatoire(false)
        setAffOrdre(1)
    }

    function loadAffectations(codeForm: string) {
        setLoadingAffect(true)
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'AFFECTATIONS',
            CodeForm: codeForm,
            CodeAbonne: user.CodeAbonne
        })
            .then((res: any) => {
                setLoadingAffect(false)
                if (res.StatutCode == 200) {
                    const list = Array.isArray(res.Result) ? res.Result : []
                    setAffectations(list)
                    setAffOrdre(list.length + 1)
                }
                else {
                    setAffectations([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoadingAffect(false)
                alert.error('Error', error?.message || error)
                console.log(error);
            })
    }

    function attacher() {
        if (!affFiliale && !affService && !affOperation) {
            alert.warning('Sélectionnez au moins une cible : filiale, service ou opération')
            return
        }
        setSavingAffect(true)
        // ATTACH : cibles optionnelles — omises plutôt que null
        const payload: any = {
            Option: 'ATTACH',
            CodeForm: formAffect?.CodeForm,
            CodeAbonne: user.CodeAbonne,
            Obligatoire: affObligatoire,
            Ordre: Number(affOrdre) || 0,
        }
        if (affFiliale) payload.CodeFiliale = affFiliale
        if (affService) payload.CodeS = affService
        if (affOperation) payload.CodeOperation = affOperation
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, payload)
            .then((res: any) => {
                setSavingAffect(false)
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    resetAffectForm()
                    loadAffectations(formAffect?.CodeForm)
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setSavingAffect(false)
                alert.error('Error', error?.message || error)
            })
    }

    function detacher(a: any) {
        setSavingAffect(true)
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'DETACH',
            AffectationId: a.Id,
            CodeAbonne: user.CodeAbonne,
        })
            .then((res: any) => {
                setSavingAffect(false)
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    loadAffectations(formAffect?.CodeForm)
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setSavingAffect(false)
                alert.error('Error', error?.message || error)
            })
    }

    // ---------- Réponses ----------
    function loadReponses(f: any) {
        setFormulaireReponses(f)
        setOpenReponses(true)
        setLoadingReponses(true)
        setReponses([])
        // Reponses : filtre {CodeAbonne, CodeForm} (vue Back Office)
        postApi(datas.API_LINKs.Formulaires.Reponses, {
            CodeAbonne: user.CodeAbonne,
            CodeForm: f?.CodeForm,
        })
            .then((res: any) => {
                setLoadingReponses(false)
                if (res.StatutCode == 200) {
                    const list = Array.isArray(res.Result) ? res.Result : []
                    setReponses(list.map((r: any, i: number) => ({ id: r?.Id ?? i, ...r })))
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoadingReponses(false)
                alert.error('Error', error?.message || error)
            })
    }

    function handleRowClick(e: any) {
        setFormSelected(dataTri.find((ar: any) => (ar.id == e.id)))
    }

    /** POST Imprimer — PDF généré côté serveur (QuestPDF), Base64 → Blob → nouvel onglet. */
    function imprimer(f: any) {
        setLoading(true)
        postApi(datas.API_LINKs.Formulaires.Imprimer, {
            CodeForm: f?.CodeForm,
            CodeAbonne: user.CodeAbonne,
            Orientation: 'PORTRAIT',
        })
            .then((res: any) => {
                setLoading(false)
                if (res?.StatutCode == 200 && res?.Result?.Base64) {
                    const bytes = Uint8Array.from(atob(res.Result.Base64), (c) => c.charCodeAt(0))
                    const blob = new Blob([bytes], { type: res.Result.ContentType || 'application/pdf' })
                    window.open(URL.createObjectURL(blob), '_blank')
                } else {
                    alert.warning(res?.Message || 'PDF non généré')
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error?.message || error)
            })
    }

    const columns: DataTableColumn[] =
        [
            { field: 'CodeForm', headerName: 'Code', width: 110 },
            { field: 'Libelle', headerName: 'Libellé', width: 180 },
            { field: 'Description', headerName: 'Description', width: 240 },
            {
                field: 'DateCreation', headerName: 'Date création', width: 150,
                renderCell: ({ value }) => <span>{formatDate(value)}</span>,
            },
            {
                field: 'Actions', headerName: 'Actions', width: 220, sortable: false,
                renderCell: ({ row }) => (
                    <div className="flex items-center gap-1">
                        <IconButton tooltip="Modifier" color="primary" size={30}
                            onClick={(e) => { e.stopPropagation(); openDrawer(row) }}>
                            <Pencil size={15} />
                        </IconButton>
                        <IconButton tooltip="Affectations" color="warning" size={30}
                            onClick={(e) => { e.stopPropagation(); openAffectations(row) }}>
                            <Link2 size={15} />
                        </IconButton>
                        <IconButton tooltip="Imprimer le PDF" color="primary" size={30}
                            onClick={(e) => { e.stopPropagation(); imprimer(row) }}>
                            <Printer size={15} />
                        </IconButton>
                        <IconButton tooltip="Réponses" color="success" size={30}
                            onClick={(e) => { e.stopPropagation(); loadReponses(row) }}>
                            <Eye size={15} />
                        </IconButton>
                        <IconButton tooltip="Supprimer" color="danger" size={30}
                            onClick={(e) => { e.stopPropagation(); setToDelete(row); setOpenDelete(true) }}>
                            <Trash2 size={15} />
                        </IconButton>
                    </div>
                ),
            },
        ];

    const reponsesCols: DataTableColumn[] = [
        { field: 'Id', headerName: 'ID', width: 60 },
        { field: 'CodeDemande', headerName: 'Demande', width: 120 },
        { field: 'CodeOperationLigne', headerName: 'Opération', width: 110 },
        { field: 'CodeClient', headerName: 'Client', width: 110 },
        {
            field: 'DateSaisie', headerName: 'Date saisie', width: 140,
            renderCell: ({ value }) => <span>{formatDate(value)}</span>,
        },
        {
            field: 'ReponseJson', headerName: 'Réponses', width: 280,
            renderCell: ({ value }) => <ReponseCell raw={value} />,
        },
    ];

    // Services filtrés par la filiale choisie dans le formulaire d'affectation
    const servicesFiltres = services.filter((s: any) => !affFiliale || s.CodeFiliale == affFiliale)

    return (
        <Spin spinning={loading} >
            <PageHeader
                title="Formulaires dynamiques"
                subtitle="Formulaires KYC rattachés aux filiales, services et opérations, et consultation des réponses"
                actions={
                    <Button variant='primary' startIcon={<Plus size={16} />} onClick={() => openDrawer()}>
                        Nouveau formulaire
                    </Button>
                }
            />
            <div className="eb-table-wrap">
                <DataTable onRowClick={handleRowClick} rows={dataTri} height={300} columns={columns} searchable emptyText="Aucun formulaire" />
            </div>
            <TableStatusBar count={dataTri.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'Code formulaire', value: formSelected?.CodeForm },
                { label: 'Libellé', value: formSelected?.Libelle },
                { label: 'Date création', value: formatDate(formSelected?.DateCreation) },
            ]} />

            <FormDrawer
                open={open}
                onClose={handleClose}
                title={formulaire ? `Modification du formulaire ${formulaire?.Libelle || ''}` : "Nouveau formulaire"}
                hint="Définissez les informations du formulaire et ses champs, dans l'ordre d'affichage."
            >
                <Spin spinning={detailLoading}>
                    <form onSubmit={onSubmit}>
                        <FormSection title="Informations" subtitle="Identifiant, libellé et description du formulaire">
                            <Input
                                required
                                type='text'
                                label={'Code du formulaire'}
                                placeholder={"Ex : KYC_CLIENT"}
                                value={codeForm}
                                disabled={!!formulaire}
                                helperText={formulaire ? "Le code identifie le formulaire et n'est pas modifiable" : undefined}
                                onChange={(e) => setCodeForm(e.target.value)} />
                            <Input
                                required
                                type='text'
                                label={'Libellé'}
                                placeholder={"Entrez le libellé du formulaire"}
                                value={libelle}
                                onChange={(e) => setLibelle(e.target.value)} />
                            <Textarea
                                label={'Description'}
                                placeholder={"Décrivez l'objet du formulaire"}
                                value={description}
                                onChange={(e: any) => setDescription(e.target.value)}
                                rows={3} />
                        </FormSection>

                        <FormSection title="Champs" subtitle="Champs du formulaire, dans l'ordre d'affichage">
                            <div className="flex flex-col gap-3" style={{ gridColumn: '1 / -1' }}>
                                {champs.length === 0 && (
                                    <p className="text-xs text-[var(--eb-text-muted)]">Aucun champ défini. Cliquez sur « Ajouter un champ ».</p>
                                )}
                                {champs.map((c, i) => (
                                    <div key={i} className="rounded-xl border border-[var(--eb-border)] p-3 flex flex-col gap-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-[var(--eb-text-muted)]">Champ {i + 1}</span>
                                            <div className="flex items-center gap-1">
                                                <IconButton type="button" tooltip="Monter" size={26} disabled={i === 0}
                                                    onClick={() => moveChamp(i, -1)}>
                                                    <ArrowUp size={14} />
                                                </IconButton>
                                                <IconButton type="button" tooltip="Descendre" size={26} disabled={i === champs.length - 1}
                                                    onClick={() => moveChamp(i, 1)}>
                                                    <ArrowDown size={14} />
                                                </IconButton>
                                                <IconButton type="button" tooltip="Supprimer le champ" color="danger" size={26}
                                                    onClick={() => removeChamp(i)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input
                                                type='text'
                                                label={'Code du champ'}
                                                placeholder={"Ex : NUM_PIECE"}
                                                value={c.CodeChamp}
                                                onChange={(e) => updateChamp(i, { CodeChamp: e.target.value })} />
                                            <Input
                                                type='text'
                                                label={'Libellé du champ'}
                                                placeholder={"Ex : Numéro de pièce"}
                                                value={c.Libelle}
                                                onChange={(e) => updateChamp(i, { Libelle: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Select
                                                label="Type"
                                                value={c.TypeChamp}
                                                onChange={(v: string) => updateChamp(i, { TypeChamp: v as TypeChamp })}
                                                options={TYPE_CHAMPS} />
                                            <Input
                                                type='text'
                                                label={'Valeur par défaut'}
                                                placeholder={"Optionnel"}
                                                value={c.ValeurDefaut}
                                                onChange={(e) => updateChamp(i, { ValeurDefaut: e.target.value })} />
                                        </div>
                                        <Input
                                            type='text'
                                            label={'Placeholder'}
                                            placeholder={"Texte d'aide dans le champ"}
                                            value={c.Placeholder}
                                            onChange={(e) => updateChamp(i, { Placeholder: e.target.value })} />
                                        <RegexField
                                            value={c.RegexValidation}
                                            onChange={(v: string) => updateChamp(i, { RegexValidation: v })} />
                                        {c.TypeChamp === 'SELECT' && (
                                            <OptionsPanier
                                                options={parseOptions(c.OptionsJson)}
                                                onChange={(opts) => updateChamp(i, { OptionsJson: JSON.stringify(opts) })} />
                                        )}
                                        <ChampPreview champ={c} />
                                        <Checkbox
                                            checked={c.Obligatoire}
                                            onChange={(v: boolean) => updateChamp(i, { Obligatoire: v })}
                                            label="Champ obligatoire" />
                                    </div>
                                ))}
                                <Button type='button' variant='outline' size='sm' startIcon={<Plus size={14} />} onClick={addChamp}>
                                    Ajouter un champ
                                </Button>
                            </div>
                        </FormSection>

                        <FormActions>
                            <Button variant='outline' type='button' startIcon={<Eye size={16} />} onClick={() => setPreviewOpen(true)}>
                                Aperçu
                            </Button>
                            <Button variant='secondary' type='submit' loading={saving} startIcon={<Save size={16} />}>Enregistrer</Button>
                        </FormActions>
                    </form>
                </Spin>
            </FormDrawer>

            {/* Aperçu global — le formulaire tel que rendu côté client */}
            <FormPreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                libelle={libelle}
                description={description}
                champs={champs}
            />

            <FormDrawer
                open={openAffect}
                onClose={() => { setOpenAffect(false); setFormAffect(null) }}
                title={`Affectations — ${formAffect?.Libelle || formAffect?.CodeForm || ''}`}
                hint="Rattachez ce formulaire à une filiale, un service et/ou une opération. Au moins une cible est requise pour chaque affectation."
                width={560}
            >
                <Spin spinning={loadingAffect}>
                    {/* Liste des affectations existantes */}
                    <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                    <th className="px-3 py-2 font-semibold">Filiale</th>
                                    <th className="px-3 py-2 font-semibold">Service</th>
                                    <th className="px-3 py-2 font-semibold">Opération</th>
                                    <th className="px-3 py-2 font-semibold">Obligatoire</th>
                                    <th className="px-3 py-2 font-semibold w-16">Ordre</th>
                                    <th className="px-3 py-2 font-semibold w-16">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {affectations.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                            Aucune affectation
                                        </td>
                                    </tr>
                                )}
                                {affectations.map((a: any, i: number) => (
                                    <tr key={a.Id ?? i} className="border-t border-[var(--eb-border)]">
                                        <td className="px-3 py-2">{a.CodeFiliale || '—'}</td>
                                        <td className="px-3 py-2">{a.CodeS || '—'}</td>
                                        <td className="px-3 py-2">{a.CodeOperation || '—'}</td>
                                        <td className="px-3 py-2">
                                            <Badge color={a.Obligatoire ? 'green' : 'gray'}>
                                                {a.Obligatoire ? 'Oui' : 'Non'}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2">{a.Ordre ?? '—'}</td>
                                        <td className="px-3 py-2">
                                            <IconButton tooltip="Détacher" color="danger" size={26} disabled={savingAffect}
                                                onClick={() => detacher(a)}>
                                                <Unlink size={14} />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Formulaire d'attachement */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <Select
                            label="Filiale"
                            value={affFiliale}
                            onChange={(v: string) => { setAffFiliale(v); setAffService(''); }}
                            options={[
                                { value: '', label: '— Aucune —' },
                                ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                            ]}
                        />
                        <Select
                            label="Service"
                            value={affService}
                            onChange={setAffService}
                            options={[
                                { value: '', label: '— Aucun —' },
                                ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
                            ]}
                        />
                        <Select
                            label="Opération"
                            value={affOperation}
                            onChange={setAffOperation}
                            options={[
                                { value: '', label: '— Aucune —' },
                                ...operations.map((o: any) => ({ value: o.CodeOperation, label: `${o.CodeOperation} - ${o.Libelle || ''}` }))
                            ]}
                        />
                        <Input
                            label="Ordre"
                            type="number"
                            min={0}
                            value={affOrdre}
                            onChange={(e: any) => setAffOrdre(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Switch checked={affObligatoire} onChange={setAffObligatoire} />
                            <span className="text-sm text-[var(--eb-text)]">Formulaire obligatoire</span>
                        </div>
                        <Button variant='secondary' size='sm' loading={savingAffect} startIcon={<Link2 size={15} />} onClick={attacher}>
                            Attacher
                        </Button>
                    </div>
                </Spin>
            </FormDrawer>

            <Modal
                open={openDelete}
                onClose={() => { setOpenDelete(false); setToDelete(null) }}
                title="Supprimer le formulaire"
                footer={
                    <>
                        <Button variant='outline' onClick={() => { setOpenDelete(false); setToDelete(null) }}>Annuler</Button>
                        <Button variant='danger' onClick={confirmDelete} startIcon={<Trash2 size={15} />}>Supprimer</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Voulez-vous vraiment supprimer le formulaire <strong>{toDelete?.Libelle}</strong> ({toDelete?.CodeForm}) ?
                    Cette action est irréversible.
                </p>
            </Modal>

            <Modal
                open={openReponses}
                onClose={() => setOpenReponses(false)}
                title={`Réponses — ${formulaireReponses?.Libelle || ''}`}
                width={900}
            >
                <Spin spinning={loadingReponses}>
                    <DataTable
                        rows={reponses}
                        columns={reponsesCols}
                        searchable
                        height={380}
                        emptyText="Aucune réponse enregistrée pour ce formulaire" />
                </Spin>
            </Modal>
        </Spin>
    );
}
